import React, { useState } from "react";
import { LuMail, LuMessageSquare, LuCheck } from "react-icons/lu";
import PublicNav, { PublicFooter } from "../components/PublicNav";
import SEO, { BASE_URL } from "../components/SEO";
import { Card, Field, Btn, Note, Select } from "../components/Ui";
import "./contact.css";

/* Public contact form. Posts to the shared all_user_query_forms_v2 endpoint,
   which stores the submission in public.all_applications_user_queries keyed by
   app_name so the team can triage CareerSavvy enquiries. The endpoint is
   CORS-gated to careersavvy.ai + localhost, so no auth header is needed. */
const CONTACT_API = "https://us-east1-foursssolutions.cloudfunctions.net/all_user_query_forms_v2";
const SUPPORT_EMAIL = "support@careersavvy.ai";

const TOPICS = [
  { value: "General Inquiry", label: "General inquiry" },
  { value: "Product Support", label: "Product support" },
  { value: "Partnership", label: "Partnership / business" },
  { value: "Feedback (Contact)", label: "Feedback / suggestion" },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Contact() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [topic, setTopic] = useState("General Inquiry");
  const [message, setMessage] = useState("");
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    name: "Contact CareerSavvy",
    url: `${BASE_URL}/contact`,
    publisher: {
      "@type": "Organization",
      name: "CareerSavvy",
      email: SUPPORT_EMAIL,
    },
  };

  async function onSubmit(e) {
    e.preventDefault();
    const next = {};
    if (!email.trim()) next.email = "Email is required";
    else if (!EMAIL_RE.test(email.trim())) next.email = "Enter a valid email address";
    if (!message.trim()) next.message = "Please tell us how we can help";
    setErrors(next);
    if (Object.keys(next).length) return;

    setStatus("sending");
    const feedbackText = name.trim() ? `From: ${name.trim()}\n\n${message.trim()}` : message.trim();
    try {
      const res = await fetch(CONTACT_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim(),
          contact_number: phone.trim(),
          request_type: topic,
          feedback_text: feedbackText,
          app_name: "careersavvy",
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (data && data.status === "error") throw new Error(data.message || "Submission failed");
      setStatus("sent");
      setName("");
      setEmail("");
      setPhone("");
      setTopic("General Inquiry");
      setMessage("");
    } catch (err) {
      console.error("Contact submit failed:", err);
      setStatus("error");
    }
  }

  return (
    <div className="csx">
      <SEO
        title="Contact Us"
        description="Get in touch with the CareerSavvy team about AI job search, resume tailoring, interview practice, partnerships or support."
        canonicalPath="/contact"
        breadcrumbs={[{ label: "Contact", path: "/contact" }]}
        jsonLd={jsonLd}
      />
      <div className="csx-page">
        <PublicNav />

        <main>
          <section className="land-section">
            <div className="wrap contact-wrap">
              <div className="contact-intro stack-3">
                <div className="stack-2">
                  <h1 className="t-h1">Get in touch</h1>
                  <p className="t-sm">
                    Questions, feedback, or partnership ideas — we'd love to hear from you. Fill out the
                    form and the CareerSavvy team will get back to you, usually within one business day.
                  </p>
                </div>
                <div className="contact-cards stack-2">
                  <div className="contact-line">
                    <span className="contact-ic"><LuMail size={18} /></span>
                    <div>
                      <div className="t-label">Email us</div>
                      <a href={`mailto:${SUPPORT_EMAIL}`} className="t-accent" style={{ fontWeight: 600 }}>
                        {SUPPORT_EMAIL}
                      </a>
                    </div>
                  </div>
                  <div className="contact-line">
                    <span className="contact-ic"><LuMessageSquare size={18} /></span>
                    <div>
                      <div className="t-label">Already a member?</div>
                      <p className="t-xs">
                        Use the in-app <strong>Feedback</strong> button to report a bug or request a feature.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <Card className="contact-card">
                {status === "sent" ? (
                  <div className="contact-done stack-3">
                    <span className="contact-done-ic"><LuCheck size={26} /></span>
                    <div className="stack-2">
                      <h2 className="t-h3">Thanks — your message is on its way</h2>
                      <p className="t-sm">
                        We've received your message and will reply to your email shortly. In the meantime,
                        feel free to explore your matches and resume tools.
                      </p>
                    </div>
                    <div>
                      <Btn variant="quiet" onClick={() => setStatus("idle")}>
                        Send another message
                      </Btn>
                    </div>
                  </div>
                ) : (
                  <form className="stack-3" onSubmit={onSubmit} noValidate>
                    {status === "error" ? (
                      <Note tone="danger">
                        Something went wrong sending your message. Please try again, or email us directly at{" "}
                        <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
                      </Note>
                    ) : null}

                    <div className="grid-2" style={{ gap: 12 }}>
                      <Field label="Name">
                        <input
                          className="input"
                          value={name}
                          placeholder="Your name"
                          onChange={(e) => setName(e.target.value)}
                        />
                      </Field>
                      <Field label="Email" error={errors.email}>
                        <input
                          className="input"
                          type="email"
                          autoComplete="email"
                          value={email}
                          placeholder="you@example.com"
                          aria-invalid={!!errors.email}
                          onChange={(e) => setEmail(e.target.value)}
                        />
                      </Field>
                    </div>
                    <div className="grid-2" style={{ gap: 12 }}>
                      <Field label="Phone (optional)">
                        <input
                          className="input"
                          value={phone}
                          placeholder="+1 555 000 0000"
                          onChange={(e) => setPhone(e.target.value)}
                        />
                      </Field>
                      <Field label="Topic">
                        <Select
                          value={topic}
                          onChange={(e) => setTopic(e.target.value)}
                          options={TOPICS}
                          ariaLabel="Topic"
                        />
                      </Field>
                    </div>
                    <Field label="Message" error={errors.message}>
                      <textarea
                        className="input contact-textarea"
                        value={message}
                        rows={6}
                        placeholder="How can we help?"
                        aria-invalid={!!errors.message}
                        onChange={(e) => setMessage(e.target.value)}
                      />
                    </Field>
                    <div>
                      <Btn type="submit" className="btn-mint btn-lg" loading={status === "sending"}>
                        Send message
                      </Btn>
                    </div>
                    <p className="t-xs">
                      By submitting this form you agree to our{" "}
                      <a href="/privacy" className="t-accent">Privacy Policy</a>.
                    </p>
                  </form>
                )}
              </Card>
            </div>
          </section>
        </main>

        <PublicFooter />
      </div>
    </div>
  );
}
